import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import {
  COMPLETE_REQUIRED_FIELDS,
  invalidLeafCount,
  requiredFieldsNotice,
  showsSubmittedError,
} from './form-error-reveal';

export { COMPLETE_REQUIRED_FIELDS, invalidLeafCount, requiredFieldsNotice };

/**
 * Material's default `ErrorStateMatcher` flips a control into the error
 * state as soon as it becomes `touched`. The Figma design language we
 * adopted for the new CRM screens does NOT show red borders just because
 * the user has tabbed past an empty required input. Errors should only
 * appear after the user has actually typed (and then deleted) something,
 * after they've submitted the form, or after Next/Save called
 * `markAllAsTouched()` (that attempt is remembered here — marking dirty
 * would also flip unsaved-changes guards).
 *
 * Register globally via:
 *
 *   { provide: ErrorStateMatcher, useClass: OnDirtyOrSubmittedErrorMatcher }
 *   installRevealedErrorState()
 */
const revealedControls = new WeakSet<AbstractControl>();
const REVEAL_PATCH = Symbol('ewentsRevealedErrors');

export function installRevealedErrorState(): void {
  const proto = AbstractControl.prototype as AbstractControl & {
    [REVEAL_PATCH]?: boolean;
    markAllAsTouched: (opts?: { emitEvent?: boolean }) => void;
  };
  if (proto[REVEAL_PATCH]) return;
  proto[REVEAL_PATCH] = true;

  const originalTouched = proto.markAllAsTouched;
  proto.markAllAsTouched = function (this: AbstractControl, opts?: { emitEvent?: boolean }) {
    revealFormErrors(this);
    return originalTouched.call(this, opts);
  };

  // FormControl / FormGroup / FormArray override reset and do not call
  // AbstractControl.reset, so the patch has to sit on those prototypes.
  for (const ctor of [FormControl, FormGroup, FormArray]) {
    const resetProto = ctor.prototype as { reset: (...args: unknown[]) => void };
    const originalReset = resetProto.reset;
    resetProto.reset = function (this: AbstractControl, ...args: unknown[]) {
      const result = originalReset.apply(this, args);
      revealedControls.delete(this);
      return result;
    };
  }
}

/**
 * Wizards that touch a step subset (not `markAllAsTouched`) must call this so
 * `OnDirtyOrSubmittedErrorMatcher` paints red borders. Tabbing alone still does not.
 */
export function revealFormErrors(control: AbstractControl): void {
  revealedControls.add(control);
  for (const child of childControls(control)) revealFormErrors(child);
}

/** Touch + reveal named controls (step Next without marking the whole form). */
export function markNamedControlsRevealed(form: AbstractControl, names: readonly string[]): void {
  for (const name of names) {
    const control = form.get(name);
    if (!control) continue;
    revealFormErrors(control);
    control.markAsTouched();
  }
}

export class OnDirtyOrSubmittedErrorMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    if (!control) return false;
    return showsSubmittedError({
      invalid: control.invalid,
      dirty: control.dirty,
      submitted: !!form?.submitted,
      revealed: revealedControls.has(control),
    });
  }
}

function childControls(control: AbstractControl): AbstractControl[] {
  const group = control as AbstractControl & {
    controls?: Record<string, AbstractControl> | AbstractControl[];
  };
  if (!group.controls) return [];
  return Array.isArray(group.controls) ? group.controls : Object.values(group.controls);
}
